import "./TasksBoard.css";

import { useContext } from "react";

import { ArrowFullLeftDarkIcon, RoundedPlusSignIcon } from "../../../../../assets";
import { OrganisationTypeConstants, WorkflowOrganisationAccessLevel } from "../../../../../constants";
import { ProjectPhase } from "../../../../../constants/project-phase";
import { ProjectDates, ProjectPhaseStatus } from "../../../../../models";
import { AssetManagerTemplate, DeveloperTemplate, EditDatesPanel, EditDealDatesPanel } from "../../../../../templates";
import { UpcomingMilestonesTaskPanel } from "../../../../../templates/UpcomingMilestonesTaskPanel";
import { WorkflowTaskPanel } from "../../../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../../../useAuth";
import { stringKeyToSentenceCase } from "../../../../../utils";
import {
  Button,
  Column,
  ProfileMenu,
  ProfileMenuItem,
  QuickActionsMenu,
  Row,
  TasksProgressKPI,
  Toggle,
  UpcomingMilestones,
  UserHeader,
} from "../../../../../widget";
import { DealStagePill } from "../../../../asset-manager/deal/components";
import {
  AddStepModal,
  CompletePhaseModal,
  CompleteStageModal,
  DealTasksBoardBanner,
  NextPreviousPhaseButtons,
  ProjectTasksBoardBanner,
  RemoveWorkflowModal,
  WorkflowPermissionsPanel,
  WorkflowSteps,
} from "./components";
import { TasksBoardContext } from "./TasksBoardContext";
import { useTasksBoard } from "./useTasksBoard";

const getProjectPhaseStatus = (phase: string, phaseDates: ProjectDates | undefined): string | null => {
  switch (phase) {
    case ProjectPhase.feasibility:
      return phaseDates?.feasibility?.status ?? ProjectPhaseStatus.NOT_STARTED;
    case ProjectPhase.design:
      return phaseDates?.design?.status ?? ProjectPhaseStatus.NOT_STARTED;
    case ProjectPhase.implementation:
      return phaseDates?.implementation?.status ?? ProjectPhaseStatus.NOT_STARTED;
    case ProjectPhase.ongoingMonitoring:
      return phaseDates?.ongoingMonitoring?.overallStatus ?? ProjectPhaseStatus.NOT_STARTED;
    default:
      return null;
  }
};

const getProjectPhaseStages = (
  phaseDates: ProjectDates | undefined,
  orderedPhases: string[]
): { key: string; label: string; status?: string | null }[] =>
  orderedPhases.map((phase) => ({
    key: phase,
    label: stringKeyToSentenceCase(phase),
    status: getProjectPhaseStatus(phase, phaseDates),
  }));

const TasksBoard = (): JSX.Element => {
  const { currentUserType } = useAuth();
  const {
    tasksBoard,
    objectName,
    quickActionsMenuOptionsList,
    showQuickActionsMenu,
    workflowOrganisations,
    workflow,

    showComplete,
    handleShowCompleteChange,

    showAddWorkflowStepPanel,
    setShowAddWorkflowStepPanel,

    showRemoveWorkflowModal,
    setShowRemoveWorkflowModal,

    showEditDatesPanel,
    closeEditDatesPanel,
    handleDealDatesSaved,

    showUpcomingMilestonesPanel,
    setShowUpcomingMilestonesPanel,

    showWorkflowTaskPanel,
    setShowWorkflowTaskPanel,

    selectedPanelTask,
    setSelectedPanelTask,

    projectDates,
    setProjectDates,

    refreshTasksBoard,
    shouldRefreshTasksBoard,

    handleCloseWorkflowTaskPanel,
    setShowQuickActionsMenu,
    hasAccessLevelAndCanEditTemplate,

    handleBackClick,
    goBackText,
    upcomingMilestones,

    toggleProgress,
    taskProgress,
    onToggle,
    showWorkflowPermissionsPanel,
    setShowWorkflowPermissionsPanel,
    isDealWorkflow,
    dealStages,
    currentDealStage,
    handleDealStageChange,
  } = useTasksBoard();
  const {
    currentPhase,
    deal,
    handlePhaseChange,
    workflowStageOptions,
    workflowStageLabel,
    isRepeatableWorkflowStage,
    workflowTemplatePhases,
  } = useContext(TasksBoardContext);

  const orderedProjectPhases =
    workflowTemplatePhases.length > 0
      ? workflowTemplatePhases.map((item) => item.phase)
      : (Object.values(ProjectPhase) as string[]);
  const projectPhaseStages = getProjectPhaseStages(projectDates?.dates, orderedProjectPhases);
  const selectedDealStageLabel =
    dealStages.find((stage) => stage.key === currentDealStage)?.label ??
    (currentDealStage ? stringKeyToSentenceCase(currentDealStage) : (dealStages[0]?.label ?? ""));

  const content = (
    <div className="TasksBoardContainer">
      <Row spacingV="ml">
        <Column span={12}>
          {currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? (
            <div className="HeaderContainer">
              <Button
                text={goBackText}
                variant="secondary"
                icon={<ArrowFullLeftDarkIcon />}
                iconPosition="left"
                onClick={handleBackClick}
                hasNoPadding
                minWidth={false}
              />
            </div>
          ) : (
            <UserHeader backButtonText={goBackText} handleBackClick={handleBackClick} />
          )}
        </Column>
      </Row>
      <Row spacingV="ml" justify="between">
        <Column span={12}>
          <div className="TasksBoardRow">
            <h1>{`${objectName} tasks board`}</h1>
            {quickActionsMenuOptionsList.length > 0 && (
              <div>
                <QuickActionsMenu onClick={() => setShowQuickActionsMenu(true)} />
                <ProfileMenu
                  show={showQuickActionsMenu}
                  onClose={() => setShowQuickActionsMenu(false)}
                  position={{ top: "0" }}
                >
                  {quickActionsMenuOptionsList.map((el) => (
                    <ProfileMenuItem
                      key={el.id}
                      profileMenuItem={el}
                      onClick={() => {
                        if (el.action) {
                          el.action();
                        }
                        setShowQuickActionsMenu(false);
                      }}
                    />
                  ))}
                </ProfileMenu>
              </div>
            )}
          </div>
        </Column>
      </Row>
      <Row spacingV="xl">
        <Column span={12}>
          <p className="body1">{`Manage the workflow steps and tasks for your chosen ${isDealWorkflow ? "deal" : "project"}`}</p>
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span="6_half">
          {taskProgress && <TasksProgressKPI data={taskProgress} toggleProgress={toggleProgress} onToggle={onToggle} />}
        </Column>
        <Column span="5_half">
          <UpcomingMilestones milestones={upcomingMilestones} onViewAll={() => setShowUpcomingMilestonesPanel(true)} />
        </Column>
      </Row>
      <Row spacingV="ml">
        <Column span={12}>
          <div className="TasksBoardRow">
            {isDealWorkflow ? (
              <div className="TasksBoardDealStageSelectorContainer">
                <h3>Select stage</h3>
                <DealStagePill
                  label={selectedDealStageLabel}
                  selectedStageLabel={currentDealStage}
                  stages={dealStages}
                  onStageClick={(stage) => handleDealStageChange(stage.key)}
                />
              </div>
            ) : (
              <div className="TasksBoardDealStageSelectorContainer">
                <h3>Select phase</h3>
                <DealStagePill
                  label={stringKeyToSentenceCase(currentPhase)}
                  selectedStageLabel={currentPhase}
                  stages={projectPhaseStages}
                  onStageClick={(stage) => handlePhaseChange(stage.key as ProjectPhase)}
                />
              </div>
            )}

            <div className="TasksBoardRowRight">
              <div className="TasksBoardRowRightToggleWrapper">
                <Toggle
                  label={`${showComplete ? "Hide" : "Show"} complete`}
                  labelAlignment="left"
                  value={showComplete}
                  onChange={() => handleShowCompleteChange()}
                  variant="primary"
                />
              </div>
              {hasAccessLevelAndCanEditTemplate(WorkflowOrganisationAccessLevel.Contributor) && (
                <Button
                  text="Add step"
                  variant="primary"
                  hasNoPadding
                  minWidth={false}
                  size="small"
                  icon={<RoundedPlusSignIcon className="RoundedPlusSignIcon" />}
                  onClick={() => setShowAddWorkflowStepPanel(true)}
                />
              )}
            </div>
          </div>
        </Column>
      </Row>
      {isDealWorkflow ? <DealTasksBoardBanner /> : <ProjectTasksBoardBanner />}
      <Row spacingV="ml">
        <Column span={12}>
          <WorkflowSteps handleAddWorkflowStep={() => setShowAddWorkflowStepPanel(true)} />
        </Column>
      </Row>
      {!isDealWorkflow && (
        <Row spacingV="ll">
          <Column span={12}>
            <NextPreviousPhaseButtons
              currentPhase={currentPhase}
              handlePhaseChange={handlePhaseChange}
              phaseOrder={orderedProjectPhases as ProjectPhase[]}
            />
          </Column>
        </Row>
      )}
      <AddStepModal
        show={showAddWorkflowStepPanel}
        closeModal={() => setShowAddWorkflowStepPanel(false)}
        workflowUuid={tasksBoard?.results[0].uuid}
      />
      <RemoveWorkflowModal show={showRemoveWorkflowModal} closeModal={() => setShowRemoveWorkflowModal(false)} />
      {!isDealWorkflow && <CompletePhaseModal />}
      {isDealWorkflow && <CompleteStageModal />}
      {projectDates && (
        <EditDatesPanel
          data={projectDates}
          setData={setProjectDates}
          show={showEditDatesPanel}
          onClose={closeEditDatesPanel}
        />
      )}
      {isDealWorkflow && (
        <EditDealDatesPanel
          deal={deal}
          workflowTemplatePhases={workflowTemplatePhases}
          show={showEditDatesPanel}
          onClose={closeEditDatesPanel}
          onSaved={handleDealDatesSaved}
        />
      )}
      <WorkflowPermissionsPanel
        workflowUuid={tasksBoard?.results[0].uuid}
        projectUuid={tasksBoard?.results[0].projectUuid ?? undefined}
        onClose={() => setShowWorkflowPermissionsPanel(false)}
        show={showWorkflowPermissionsPanel}
        setShow={setShowWorkflowPermissionsPanel}
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        existingWorkflowOrganisations={workflowOrganisations!}
      />
      <UpcomingMilestonesTaskPanel
        workflowUuid={workflow?.uuid ?? undefined}
        projectUuid={workflow?.projectUuid ?? undefined}
        show={showUpcomingMilestonesPanel}
        onClose={() => setShowUpcomingMilestonesPanel(false)}
        setSelectedPanelTask={setSelectedPanelTask}
        setShowWorkflowTaskPanel={setShowWorkflowTaskPanel}
        refreshTasks={refreshTasksBoard}
      />
      {selectedPanelTask && (
        <WorkflowTaskPanel
          task={selectedPanelTask}
          currentStepUuid={selectedPanelTask?.step.uuid}
          workflow={workflow}
          show={showWorkflowTaskPanel}
          phaseOptions={workflowStageOptions}
          phaseLabel={workflowStageLabel}
          isRepeatablePhase={isRepeatableWorkflowStage}
          refresh={refreshTasksBoard}
          handleRefresh={shouldRefreshTasksBoard}
          onClose={handleCloseWorkflowTaskPanel}
        />
      )}
    </div>
  );

  if (currentUserType === OrganisationTypeConstants.ASSET_MANAGER) {
    return <AssetManagerTemplate activeElement="Operations">{content}</AssetManagerTemplate>;
  }

  return <DeveloperTemplate activeElement="Workflows">{content}</DeveloperTemplate>;
};

export default TasksBoard;
